import { useMemo, useState } from 'react'
import Modal from '../common/Modal'
import SectorTag from './SectorTag'
import {
    mechanismCategories,
    mechanismCategoriesForSector,
    mechanismCategoryLabels,
    mechanismSignalsByCategory,
    mechanismSectorSlugs,
    isUniversalCategory,
} from '../../helpers/dataHelpers/mechanismCategories'
import { getSectorInfo } from '../../helpers/meetstandaard/sectorCatalog'
import ExpandMoreOutlinedIcon from '@mui/icons-material/ExpandMoreOutlined'

const ALL_TAB = 'all'

/**
 * The categories on the selected tab.
 *
 * A sector tab shows everything an organisation in that sector can use: the
 * categories tagged with the slug AND the universal ones. Psychologische
 * veiligheid is tagged '*' and is just as much part of arbeidsparticipatie as
 * praktijkleren — leaving it off the tab suggested the sector had no working
 * practice around safety. The tab stays meaningful because the two kinds are
 * rendered under separate headings.
 *
 * Delegates to the taxonomy's own selector rather than re-testing the tags here:
 * that rule is already mirrored across two trees, and a third copy is one
 * sector-scoping change away from drifting
 * (docs/pitfalls.md#dev-copied-recipe-instead-of-shared-hook).
 *
 * @param {string} tab - Active tab: 'all' or a sector slug.
 * @returns {string[]} Category keys in canonical order.
 */
const categoriesForTab = (tab) =>
    tab === ALL_TAB ? [...mechanismCategories] : mechanismCategoriesForSector([tab])

/**
 * @typedef {object} MechanismChoice
 * @property {string} category - Taxonomy category key.
 * @property {string} title - The concrete mechanism (signal) title.
 */

/**
 * Pick several werkzame mechanismen at once, grouped by category and filtered
 * by sector tab.
 *
 * Replaces adding blank rows one at a time. Mechanisms the organisation already
 * has are shown checked and disabled rather than hidden, so the list stays a
 * complete picture of the taxonomy.
 *
 * Categories open collapsed: twenty categories with ~8 kenmerken each is a wall
 * of 160 checkboxes, so the header list is the overview and the kenmerken only
 * appear once you open a category.
 *
 * @param {object} props
 * @param {boolean} props.modalOpen - Whether the modal is visible.
 * @param {(open: boolean) => void} props.setModalOpen - Opens/closes the modal.
 * @param {string[]} props.existingTitles - Mechanism titles the org already has.
 * @param {(choices: MechanismChoice[]) => void} props.onAdd - Called with every newly ticked mechanism.
 */
const AddMechanismsModal = ({ modalOpen, setModalOpen, existingTitles = [], onAdd }) => {

    // Always opens on the full list. A sector tab is an opt-in filter that drops
    // the categories belonging to another sector only.
    const [tab, setTab] = useState(ALL_TAB)
    const [selected, setSelected] = useState(/** @type {MechanismChoice[]} */ ([]))
    // Collapsed by default — an open category is opt-in, per category key. The
    // selection lives here in the modal, so collapsing a category never drops a
    // tick. AI-CONTEXT: See docs/pitfalls.md#ui-filtering-a-list-unmounts-unsaved-input
    const [openCategories, setOpenCategories] = useState(/** @type {string[]} */ ([]))

    const alreadyAdded = useMemo(
        () => new Set((existingTitles || []).map((t) => (t || '').trim()).filter(Boolean)),
        [existingTitles]
    )

    const isSelected = (category, title) =>
        selected.some((s) => s.category === category && s.title === title)

    const countSelected = (category) =>
        selected.filter((s) => s.category === category).length

    const toggle = (category, title) => {
        setSelected((prev) =>
            prev.some((s) => s.category === category && s.title === title)
                ? prev.filter((s) => !(s.category === category && s.title === title))
                : [...prev, { category, title }]
        )
    }

    const toggleCategory = (key) => {
        setOpenCategories((prev) =>
            prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
        )
    }

    const visibleCategories = categoriesForTab(tab)
    const sectorSpecific = visibleCategories.filter((key) => !isUniversalCategory(key))
    const universal = visibleCategories.filter(isUniversalCategory)

    const close = () => {
        setSelected([])
        setOpenCategories([])
        setTab(ALL_TAB)
        setModalOpen(false)
    }

    const confirm = () => {
        if (selected.length > 0) onAdd(selected)
        close()
    }

    /**
     * One collapsible category with its kenmerken.
     * @param {string} key - Taxonomy category key.
     * @returns {JSX.Element}
     */
    const renderGroup = (key) => {
        const signals = mechanismSignalsByCategory[key] || []
        const open = openCategories.includes(key)
        const chosen = countSelected(key)
        return (
            <div key={key} className='mechanism-picker-group'>
                {/* Kop OM de knop, niet erin: een <h4> in een <button> is
                    ongeldige nesting en ARIA slaat de kinderen van een knop plat,
                    waardoor de kop geen kop meer is — zonder dat een test of een
                    waarschuwing het laat merken.
                    AI-CONTEXT: See docs/pitfalls.md#ui-heading-inside-button-stops-being-a-heading */}
                <h4 className='mechanism-picker-group-heading'>
                    <button
                        type='button'
                        className='mechanism-picker-group-header'
                        aria-expanded={open}
                        onClick={() => toggleCategory(key)}
                    >
                        <ExpandMoreOutlinedIcon
                            fontSize='small'
                            className={
                                open
                                    ? 'mechanism-picker-chevron open'
                                    : 'mechanism-picker-chevron'
                            }
                        />
                        <span className='mechanism-picker-group-label'>
                            {mechanismCategoryLabels[key]}
                        </span>
                        <SectorTag category={key} />
                        <span className='mechanism-picker-group-count'>
                            {signals.length} kenmerken
                        </span>
                        {chosen > 0 && (
                            <span className='mechanism-picker-group-chosen'>
                                {chosen} gekozen
                            </span>
                        )}
                    </button>
                </h4>
                {open && (
                    <ul className='mechanism-picker-list'>
                        {signals.map((signal) => {
                            const disabled = alreadyAdded.has(signal)
                            const inputId = `mech-${key}-${signal}`
                            return (
                                <li key={signal}>
                                    <input
                                        type='checkbox'
                                        id={inputId}
                                        checked={disabled || isSelected(key, signal)}
                                        disabled={disabled}
                                        onChange={() => toggle(key, signal)}
                                    />
                                    <label htmlFor={inputId}>
                                        {signal}
                                        {disabled && (
                                            <span className='mechanism-picker-added'>al toegevoegd</span>
                                        )}
                                    </label>
                                </li>
                            )
                        })}
                    </ul>
                )}
            </div>
        )
    }

    const content = (
        <div className='mechanism-picker'>

            <div className='mechanism-picker-tabs' role='tablist'>
                <button
                    type='button'
                    role='tab'
                    aria-selected={tab === ALL_TAB}
                    className={tab === ALL_TAB ? 'mechanism-picker-tab active' : 'mechanism-picker-tab'}
                    onClick={() => setTab(ALL_TAB)}
                >
                    Alle sectoren
                </button>
                {mechanismSectorSlugs.map((slug) => (
                    <button
                        key={slug}
                        type='button'
                        role='tab'
                        aria-selected={tab === slug}
                        className={tab === slug ? 'mechanism-picker-tab active' : 'mechanism-picker-tab'}
                        onClick={() => setTab(slug)}
                    >
                        {getSectorInfo(slug).label}
                        <span className='mechanism-picker-tab-slug'>{slug}</span>
                    </button>
                ))}
            </div>

            <p className='mechanism-picker-hint'>
                {tab === ALL_TAB
                    ? 'Klik op een categorie om de mechanismen te zien en vink aan wat je wilt toevoegen. Mechanismen die je organisatie al heeft staan aangevinkt en zijn niet te wijzigen.'
                    : `Alles wat bruikbaar is voor ${getSectorInfo(tab).label}: eerst wat specifiek is voor deze sector, daaronder wat voor elke sector geldt.`}
            </p>

            {selected.length > 0 && (
                <div className='mechanism-picker-selection'>
                    <span className='mechanism-picker-selection-label'>
                        Gekozen ({selected.length})
                    </span>
                    <ul className='mechanism-picker-selection-list'>
                        {selected.map((choice) => (
                            <li key={`${choice.category}-${choice.title}`}>
                                <button
                                    type='button'
                                    className='mechanism-picker-selection-chip'
                                    onClick={() => toggle(choice.category, choice.title)}
                                    aria-label={`${choice.title} uit de selectie halen`}
                                >
                                    {choice.title}
                                    <span aria-hidden='true'>✕</span>
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {tab === ALL_TAB ? (
                visibleCategories.map(renderGroup)
            ) : (
                <>
                    {sectorSpecific.length > 0 && (
                        <section
                            className='mechanism-picker-section'
                            aria-labelledby='mechanism-picker-section-specific'
                        >
                            <h3 id='mechanism-picker-section-specific'>
                                Specifiek voor {getSectorInfo(tab).label}
                            </h3>
                            {sectorSpecific.map(renderGroup)}
                        </section>
                    )}
                    {universal.length > 0 && (
                        <section
                            className='mechanism-picker-section'
                            aria-labelledby='mechanism-picker-section-universal'
                        >
                            <h3 id='mechanism-picker-section-universal'>
                                Voor alle sectoren
                            </h3>
                            {universal.map(renderGroup)}
                        </section>
                    )}
                </>
            )}
        </div>
    )

    return (
        <Modal
            modalOpen={modalOpen}
            setModalOpen={close}
            confirm={confirm}
            confirmDisabled={selected.length === 0}
            confirmLabel={
                selected.length > 0
                    ? `${selected.length} toevoegen`
                    : 'Toevoegen'
            }
            pageHeader='Werkzame mechanismen toevoegen'
            content={content}
        />
    )
}

export default AddMechanismsModal
